/**
 * Street Gang — a carjacker van that spawns mid-race, chases the player,
 * and "steals" the car on contact. While stolen the player is frozen in
 * place as a passenger; they mash E (or the on-screen RECOVER button)
 * to fill a struggle meter. When the meter tops out the gang is shaken
 * off, the van peels away, and the player keeps racing.
 *
 * Single active gang at a time. Spawns once per race by default (mirroring
 * the A-Team one-shot pattern) — aggressive enough to be a surprise,
 * not so common it derails every run.
 */
const WORLD_W = 1280;
const WORLD_H = 820;

const SPAWN_WAIT_MS = 30_000;     // earliest any spawn can happen (user-set: "only come when you're 30 seconds in")
const THEFT_DURATION_MS = 8_000;  // freeze time if player never mashes
// User feedback (twice): "you need E×5 to escape and the van needs to be
// SLOWER than the player." Tuned generously: 4 mashes = escape, 5 mashes
// = guaranteed escape. No decay so each press only ever helps.
const MASH_PER_HIT = 0.30;        // 4 mashes × 0.30 = 1.2 (clamped to 1.0)
const MASH_DECAY = 0;             // no decay — never feel the bar slipping
const CONTACT_R = 38;
// Player base max-speed at speed_level 1 is 5.2. Setting van to 3.2 means
// the player can EASILY outrun the gang on a straight, even without boost.
// Boost (5.2 × 1.9 = 9.88) blasts through entirely.
const VAN_SPEED = 3.2;

export function tickGang(st) {
  const now = performance.now();
  // First-time init. Wait until the race has been active for SPAWN_WAIT_MS
  // so the player isn't jumped at start.
  if (!st.gang) {
    st.gang = { state: "idle", used: false, nextSpawnAt: now + SPAWN_WAIT_MS };
  }
  const g = st.gang;

  // Cancel any active jacking the moment the player is arrested — being
  // grabbed from inside a jail cell is nonsensical (user feedback).
  if ((st.player.jailedUntil || 0) > now && (g.state === "chasing" || g.state === "stolen")) {
    g.state = "fleeing";
    g.releasedAt = now;
    if (g.van) {
      // Send the van off-screen in whatever direction it was already
      // heading so it doesn't snap visually.
      g.van.vx = (g.van.vx || 0) * 1.6 || -6;
      g.van.vy = (g.van.vy || 0) * 1.6;
    }
    return;
  }

  // Decide when to spawn. Once per race. Only if the player isn't already
  // in the middle of one.
  if (g.state === "idle" && !g.used && now >= g.nextSpawnAt) {
    _spawn(st);
    g.used = true;
    return;
  }

  if (g.state === "chasing") _updateChase(st, now);
  else if (g.state === "stolen") _updateStolen(st, now);
  else if (g.state === "fleeing") _updateFleeing(st);
}

function _spawn(st) {
  const p = st.player;
  // Spawn off-screen on the side nearest the player so the van drives in.
  const fromLeft = p.x > WORLD_W / 2;
  st.gang.van = {
    x: fromLeft ? -50 : WORLD_W + 50,
    y: p.y + (Math.random() - 0.5) * 80,
    vx: fromLeft ? 5.5 : -5.5,
    vy: 0,
    angle: fromLeft ? 0 : Math.PI,
  };
  st.gang.state = "chasing";
}

function _updateChase(st, now) {
  const v = st.gang.van;
  const p = st.player;
  // Steer slowly toward the player. Tuned to be slower than a cop — see
  // VAN_SPEED — so the player can sprint past on a straightaway.
  const dx = p.x - v.x;
  const dy = p.y - v.y;
  const angle = Math.atan2(dy, dx);
  v.vx = Math.cos(angle) * VAN_SPEED;
  v.vy = Math.sin(angle) * VAN_SPEED;
  v.x += v.vx;
  v.y += v.vy;
  v.angle = angle;

  // Turbo-escape: if the player has rocket boost active when contact
  // happens, they smash through and the gang peels off. boostUntil is
  // set by the SHIFT-trigger handler in GameCanvas.
  const boostActive = (p.boostUntil || 0) > now;

  // Contact?
  if (Math.hypot(dx, dy) <= CONTACT_R) {
    if (boostActive) {
      // Player turbo'd into them — knock the van off-course and have
      // them flee instead of stealing.
      st.gang.state = "fleeing";
      st.gang.releasedAt = now;
      // Kick the van away from the player so it visibly bounces off.
      v.vx = -Math.cos(angle) * VAN_SPEED * 1.6;
      v.vy = -Math.sin(angle) * VAN_SPEED * 1.6;
      st.gang.turboEscape = true;
      return;
    }
    // Theft. Freeze the player — the main game loop checks for
    // st.gang.state === "stolen" to skip input.
    st.gang.state = "stolen";
    st.gang.stolenAt = now;
    st.gang.struggleProgress = 0;
    // Grab a rear-view mirror snapshot of player position — the gang
    // pulls up behind; player's car is drawn in their hands (visually
    // "carried").
    st.gang.heldX = p.x;
    st.gang.heldY = p.y;
    st.gang.toastShownAt = now;
  }
}

function _updateStolen(st, now) {
  const g = st.gang;
  // Passive decay so recovery requires sustained mashing.
  g.struggleProgress = Math.max(0, (g.struggleProgress || 0) - MASH_DECAY);
  // Van drives the player around during the theft — simple orbit.
  const v = g.van;
  const t = (now - g.stolenAt) / 1000;
  v.angle = (v.angle || 0) + 0.03;
  v.x = g.heldX + Math.cos(t) * 40;
  v.y = g.heldY + Math.sin(t) * 40;
  // Auto-release when the struggle bar tops out OR when the timer expires.
  if (g.struggleProgress >= 1 || now - g.stolenAt >= THEFT_DURATION_MS) {
    g.state = "fleeing";
    g.releasedAt = now;
    // Player keeps whatever their angle was. Push them slightly back
    // toward their held position.
    const p = st.player;
    p.x = g.heldX;
    p.y = g.heldY;
    p.vx = 0;
    p.vy = 0;
  }
}

function _updateFleeing(st) {
  const v = st.gang.van;
  if (!v) {
    st.gang = { ...st.gang, state: "idle" };
    return;
  }
  // Peel away off-screen.
  v.x += v.vx * 1.6;
  v.y += v.vy * 1.6;
  if (v.x < -80 || v.x > WORLD_W + 80 || v.y < -80 || v.y > WORLD_H + 80) {
    delete st.gang.van;
    st.gang.state = "idle";
  }
}

/** Called when the player presses the "struggle" key (E). */
export function gangStruggle(st) {
  if (!st.gang || st.gang.state !== "stolen") return false;
  st.gang.struggleProgress = Math.min(1, (st.gang.struggleProgress || 0) + MASH_PER_HIT);
  return true;
}

/** Convenience — is the player currently carjacked? */
export function isPlayerStolen(st) {
  return st.gang && st.gang.state === "stolen";
}

/** Fraction of the recovery meter filled (0..1). For HUD rendering. */
export function struggleProgress(st) {
  return (st.gang && st.gang.struggleProgress) || 0;
}

// ------------------------------------------------------------------
// Render — a chunky black van with 3 thug silhouettes inside.
// ------------------------------------------------------------------
export function drawGangVan(ctx, st) {
  const g = st.gang;
  if (!g || !g.van) return;
  const v = g.van;
  ctx.save();
  ctx.translate(v.x, v.y);
  ctx.rotate(v.angle || 0);
  const W = 62, H = 32;
  // Body — matte black, thin red stripe
  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(-W / 2, -H / 2, W, H);
  ctx.fillStyle = "#8B0000";
  ctx.fillRect(-W / 2, -2, W, 4);
  // Windshield — tinted
  ctx.fillStyle = "rgba(40,40,40,0.95)";
  ctx.fillRect(W / 2 - 18, -H / 2 + 4, 12, H - 8);
  // Three thug silhouettes in the passenger area
  ctx.fillStyle = "#1A1A1A";
  ctx.fillRect(-W / 2 + 8, -H / 2 + 6, 6, H - 12);
  ctx.fillRect(-W / 2 + 20, -H / 2 + 6, 6, H - 12);
  ctx.fillRect(-W / 2 + 32, -H / 2 + 6, 6, H - 12);
  // Headlights
  ctx.fillStyle = "#FFCC00";
  ctx.fillRect(W / 2 - 3, -H / 2 + 4, 3, 4);
  ctx.fillRect(W / 2 - 3, H / 2 - 8, 3, 4);
  // Wheels
  ctx.fillStyle = "#000";
  ctx.fillRect(-W / 2 + 6, -H / 2 - 3, 12, 3);
  ctx.fillRect(-W / 2 + 6, H / 2, 12, 3);
  ctx.fillRect(W / 2 - 18, -H / 2 - 3, 12, 3);
  ctx.fillRect(W / 2 - 18, H / 2, 12, 3);
  // Plate
  ctx.fillStyle = "#FFCC00";
  ctx.fillRect(-W / 2 - 3, -4, 3, 8);
  ctx.fillStyle = "#1A1A1A";
  ctx.font = "bold 6px monospace";
  ctx.textAlign = "center";
  ctx.fillText("STL", -W / 2 + 2, 2);
  ctx.restore();
}
